import React, { useEffect, useState } from "react";
import { Trophy, ShieldCheck, Mail, AtSign, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TurfTalk } from "@/api/client";
import { PageHeader, Skeleton } from "@/components/ui-kit";
import { Modal, TextField, TextArea } from "@/components/Modal";

function EditProfileModal({ open, onClose, profile, onSaved }) {
  const [teams, setTeams] = useState({ NFL: [], NCAA: [] });
  const [f, setF] = useState({
    display_name: profile.display_name || "", bio: profile.bio || "",
    public_profile: profile.public_profile !== false,
    followed_nfl_team_ids: profile.followed_nfl_team_ids || [],
    followed_ncaa_team_ids: profile.followed_ncaa_team_ids || [],
    kickoff_notif: !!profile.notifications?.followed_team_kickoff,
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) Promise.all([TurfTalk.teams("NFL"), TurfTalk.teams("NCAA")]).then(([a, b]) => setTeams({ NFL: a.teams || a || [], NCAA: b.teams || b || [] })).catch(() => {}); }, [open]);

  const toggle = (key, id) => setF((s) => ({ ...s, [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id] }));

  const save = async () => {
    setBusy(true);
    try {
      await TurfTalk.updateProfile({
        display_name: f.display_name.trim() || undefined,
        bio: f.bio,
        public_profile: f.public_profile,
        followed_nfl_team_ids: f.followed_nfl_team_ids,
        followed_ncaa_team_ids: f.followed_ncaa_team_ids,
        notifications: { ...(profile.notifications || {}), followed_team_kickoff: f.kickoff_notif },
      });
      toast.success("Profile saved");
      await onSaved();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const Chips = ({ list, key }) => (
    <div className="flex flex-wrap gap-2">
      {list.map((t) => {
        const on = f[key].includes(t.id);
        return <button key={t.id} onClick={() => toggle(key, t.id)} data-testid={`edit-team-${t.id}`} className={`px-2.5 h-8 rounded-full text-xs border ${on ? "bg-[#24362A] border-[#2F7D32] text-white" : "border-[#2A3544] text-white/60"}`}>{t.abbr}</button>;
      })}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Edit profile" testid="edit-profile-modal">
      <TextField label="Display name" value={f.display_name} onChange={(e) => setF((s) => ({ ...s, display_name: e.target.value }))} data-testid="edit-display-name" maxLength={40} />
      <TextArea label="Bio" value={f.bio} onChange={(e) => setF((s) => ({ ...s, bio: e.target.value }))} data-testid="edit-bio" maxLength={280} />
      <div className="mb-3"><div className="text-sm text-white/70 mb-1">Following (NFL)</div><Chips list={teams.NFL} key="nfl" /></div>
      <div className="mb-3"><div className="text-sm text-white/70 mb-1">Following (NCAA)</div><Chips list={teams.NCAA} key="ncaa" /></div>
      <label className="flex items-center gap-2 text-sm text-white/70 mb-2 cursor-pointer"><input type="checkbox" checked={f.public_profile} onChange={(e) => setF((s) => ({ ...s, public_profile: e.target.checked }))} className="accent-[#2F7D32]" data-testid="edit-public" /> Public profile</label>
      <label className="flex items-center gap-2 text-sm text-white/70 mb-4 cursor-pointer"><input type="checkbox" checked={f.kickoff_notif} onChange={(e) => setF((s) => ({ ...s, kickoff_notif: e.target.checked }))} className="accent-[#2F7D32]" data-testid="edit-kickoff-notif" /> Kickoff alerts for followed teams</label>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} data-testid="edit-profile-save" className="flex-1 h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        <button onClick={onClose} data-testid="edit-profile-cancel" className="px-4 h-11 rounded-lg tt-panel-3 text-white/80">Cancel</button>
      </div>
    </Modal>
  );
}

export default function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const [trophies, setTrophies] = useState(null);
  const [edit, setEdit] = useState(false);
  useEffect(() => { TurfTalk.trophyCabinet().then((r) => setTrophies(r.achievements || r.earned || [])).catch(() => setTrophies([])); }, []);
  if (!user) return <div className="max-w-4xl mx-auto px-8 py-8"><Skeleton className="h-40" /></div>;
  const p = user.profile || user;

  const stats = [
    { label: "Handle", value: `@${user.handle}` },
    { label: "Following NFL", value: (p.followed_nfl_team_ids || []).length },
    { label: "Following NCAA", value: (p.followed_ncaa_team_ids || []).length },
    { label: "Member since", value: user.created_at ? new Date(user.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Profile" right={<button onClick={() => setEdit(true)} data-testid="edit-profile-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg tt-panel text-white/80 hover:text-white text-sm"><Pencil size={15} /> Edit profile</button>} />
      <div className="relative rounded-2xl overflow-hidden mb-6 tt-rise">
        <div className="h-40" style={{ background: "linear-gradient(120deg,#17405c,#0e2233 60%,#070c11)" }} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-4 left-6 flex items-end gap-4">
          <div className="w-20 h-20 rounded-2xl bg-[#24362A] border-2 border-[#2F7D32] grid place-items-center font-display text-3xl text-white">{(p.display_name || user.handle || "?").slice(0, 2).toUpperCase()}</div>
          <div className="pb-1">
            <div className="font-display text-3xl text-white leading-none" data-testid="profile-display-name">{p.display_name || user.handle}</div>
            <div className="text-white/60 text-sm flex items-center gap-3 mt-1"><span className="inline-flex items-center gap-1"><AtSign size={13} />{user.handle}</span>{user.email_verified && <span className="inline-flex items-center gap-1 text-[#4ea152]"><ShieldCheck size={13} /> verified</span>}</div>
          </div>
        </div>
      </div>
      {p.bio && <p className="text-white/70 text-sm mb-6 -mt-2" data-testid="profile-bio">{p.bio}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (<div key={s.label} className="tt-panel rounded-xl p-4"><div className="text-[11px] tracking-wide text-white/40">{s.label}</div><div className="font-display text-2xl text-white mt-1 tabnums truncate">{s.value}</div></div>))}
      </div>
      <div className="flex items-center gap-2 mb-3"><Trophy size={16} className="text-[#F4B740]" /><h2 className="font-display tracking-wide text-lg text-white">Trophy Cabinet</h2></div>
      {trophies === null ? <Skeleton className="h-24" /> : trophies.length === 0 ? <div className="tt-panel rounded-xl p-8 text-center text-white/40 text-sm">Trophy cabinet is empty. Make picks to earn badges.</div> : (
        <div className="grid sm:grid-cols-3 gap-4">{trophies.map((t, i) => (<div key={t.id || i} className="tt-panel rounded-xl p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-[#F4B740]/15 grid place-items-center text-[#F4B740]"><Trophy size={18} /></div><div><div className="text-white text-sm">{t.name || t.title || t.id}</div><div className="text-white/40 text-xs">{t.description || ""}</div></div></div>))}</div>
      )}
      <div className="mt-8 tt-panel rounded-xl p-4 flex items-center gap-2 text-white/50 text-sm"><Mail size={15} /> {user.email}</div>
      <EditProfileModal open={edit} onClose={() => setEdit(false)} profile={p} onSaved={refreshMe} />
    </div>
  );
}
